<template>
    <div>
        <Transition v-if="isLoading"
                    name="spinner">
            <div id="cover-spin"></div>
        </Transition>

        <VLayout class="rounded-md flex-column">
            <Menubar ref='menubar'
                     class="px-2 menu-horizontal-outer"
                     :model="menuItems">
                <template #start>
                    <v-col>
                        <Image :src="menuLogo" style="height: 40px;" />
                    </v-col>
                </template>

                <template #item="{ item, props, hasSubmenu, root }">
                    <span v-if="(!item?.hidden || isCreator) && (Object.values(unprotectedMenuItems).includes(item?.fullPath?.toLowerCase().replace(/[_\s]/g, '-')) || hasPermission(item?.fullPath))">
                        <Link v-ripple
                              v-if="item.route"
                              :href="checkRoute(item.route, null, item.label)"
                              :id="item.id"
                              :style="item.itemstyle"
                              class="flex flex-row flex-nowrap menu-horizontal-inner"
                              v-bind="props.action">
                            <div inert>
                                <span :class="item.icon"
                                      :style="item.iconstyle" />
                                <span class="ml-2"
                                      :class="item.labelclass"
                                      :style="item.labelstyle">{{ __(item.label) }}</span>
                                <div v-if="item.description"
                                     class="menu-horizontal-description">
                                    <span v-html="item.description"></span>
                                </div>
                            </div>
                        </Link>

                        <div v-ripple
                             v-else
                             class="flex flex-row flex-nowrap menu-horizontal-inner"
                             :style="item.itemstyle"
                             v-bind="props.action">
                            <div class="align-items-start cursor-pointer w-100">
                                <span :class="item.icon"
                                      :style="item.iconstyle" />
                                <span class="ml-2"
                                      :class="item.labelclass"
                                      :style="item.labelstyle">{{ __(item.label) }}</span>
                                <i v-if="hasSubmenu"
                                   :class="['pi pi-angle-down text-primary', { 'pi-angle-down ml-2': root, 'pi-angle-right ml-2': !root }]"
                                   :style="item.submenustyle"
                                   style="float:right"></i>
                                <div v-if="item.description"
                                     class="menu-horizontal-description"
                                     :id="item.id">
                                    <span v-html="item.description"></span>
                                </div>
                            </div>
                        </div>
                    </span>
                </template>

                <template #end>
                    <div class="flex align-items-center">
                        <div class="flex menu-horizontal-left-part">
                            <div class="menu-horizontal-left-part-begin"></div>
                            <div class="menu-horizontal-left-part-middle"></div>
                            <div class="menu-horizontal-left-part-end">
                                <Button v-if="multiModuleApp"
                                        icon="pi pi-server"
                                        severity="primary"
                                        text
                                        size="large"
                                        v-tooltip.top="__('Select module')"
                                        @click="selectModule" />
                            </div>
                        </div>
                        <div class="session-time-header"
                             style="margin-left:20px"></div>
                    </div>
                </template>
            </Menubar>

            <VFooter class="surface-card border-top-1 surface-border"
                     app>
                <div class="w-full flex flex-wrap align-items-center justify-content-between gap-3 py-2">
                    <div class="flex flex-nowrap align-items-center gap-2">
                        <Image :src="developerLogo"
                               style="width:80px;" />
                        <span class="text-xs text-color-secondary line-height-3">Powered by © 4Evolve wersja 2.0<br>rewizja {{ systemRevision }}</span>
                    </div>

                    <div class="flex align-items-center flex-wrap gap-2">
                        <Tag v-if="premiumPeriod"
                             severity="info"
                             :value="premiumPeriod" />
                        <Tag v-if="currentDate"
                             severity="secondary"
                             :value="currentDate" />
                        <Tag v-if="deputyName"
                             severity="warning"
                             :value="__('Replaced worker') + ': ' + deputyName" />
                    </div>

                    <div class="flex flex-row flex-nowrap align-items-center gap-3">
                        <span v-if="isCreator"
                              class="admin-area-in-footer"></span>

                        <div class="flex flex-nowrap align-items-center gap-3">
                            <Link v-ripple
                                  class="href-text text-sm"
                                  :href="checkRoute('dashboard-help', null, null)">Pomoc</Link>

                            <a v-ripple
                               class="href-text text-sm"
                               href="https://harpun.4evolve.com/storage/attachments/regulamin_premiowania_2025.pdf"
                               target="_blank"
                               rel="noopener noreferrer">Regulamin</a>

                            <Link v-ripple
                                  class="href-text text-sm"
                                  :href="checkRoute('dashboard-privacy', null, null)">Polityka prywatności</Link>
                        </div>

                        <Divider layout="vertical"
                                 class="mx-1" />

                        <div id='user-name-area'
                             style="text-wrap:nowrap">
                            <Link v-ripple
                                  class="text-sm font-medium"
                                  :href="checkRoute('user-profile', { user_id: userId })">{{ userName }}</Link>
                        </div>

                        <div class="flex align-items-center">
                            <Button icon="pi pi-save"
                                    severity="primary"
                                    id="saveSessionIcon"
                                    text
                                    rounded
                                    size="large"
                                    v-tooltip.top="__('Save session')"
                                    @click="saveSession" />
                            <Button icon="pi pi-trash"
                                    severity="warning"
                                    id="removeSessionIcon"
                                    text
                                    rounded
                                    size="large"
                                    v-tooltip.top="__('Remove session')"
                                    @click="removeSession" />
                        </div>
                    </div>
                </div>
            </VFooter>

        </VLayout>

        <div class="layout-box"
             style="margin-bottom:70px">
            <slot></slot>
        </div>
        <flash-message />
    </div>
</template>

<script setup>
import Menubar from 'primevue/menubar';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Divider from 'primevue/divider';
import { Link, router, usePage } from '@inertiajs/vue3'
import { ref, onMounted, onBeforeMount, watchEffect } from "vue";
import Image from '@helper/Image.vue'
import { initFunctions } from "../../init.js";
import { __, saveAppSession, removeAppSession, hasPermission, can, checkRoute, getStoreValue, setStoreValue, getServerValue, getFromRoute } from '@pages/Helper/utils.js';
import { menuLogo, developerLogo } from '@helper/constants.js'
import axios from 'axios'
import { formatPremiumPeriod } from '@pages/Helper/custom.js'

const menuItems = ref(null);
const unprotectedMenuItems = ref({});
const userName = ref(null);
const userId = ref(null);
const multiModuleApp = ref(false);
const systemRevision = ref(getStoreValue('systemRevision'));

const premiumPeriod = ref(null);
const currentDate = ref(null)
const deputyName = ref(null);
const deputyInformation = ref(null);
const page = usePage();
const isLoading = ref(false);
const isCreator = ref(false);

//------------------------------------------------------------------------------

watchEffect(async () => {
    let savedPremiumPeriod = null
    savedPremiumPeriod = getStoreValue('premiumPeriod')
    if (!savedPremiumPeriod) {
        savedPremiumPeriod = await getServerValue('premiumPeriod')
        if (savedPremiumPeriod?.hasOwnProperty('premiumPeriod')) {
            setStoreValue('premiumPeriod', savedPremiumPeriod.premiumPeriod)
            savedPremiumPeriod = savedPremiumPeriod.premiumPeriod
        }
    }

    if (savedPremiumPeriod) {
        premiumPeriod.value = formatPremiumPeriod(savedPremiumPeriod)
    }
});

// ------------------------------------------------------------------------------

watchEffect(async () => {
    let savedDeputyName = null
    savedDeputyName = getStoreValue('deputyName')

    if (!savedDeputyName) {
        const response = await getFromRoute("worker-deputy", {}, 'post');
        savedDeputyName = response.deputy_name
        if (savedDeputyName) {
            setStoreValue('deputyName', savedDeputyName)
            deputyInformation.value = savedDeputyName
        } else {
            setStoreValue('deputyName', null)
        }
    }

    if (savedDeputyName) {
        deputyName.value = savedDeputyName
    } else {
        deputyName.value = null
    }
});

//------------------------------------------------------------------------------

watchEffect(async () => {
    currentDate.value = page.props.currentDate;
});

// ------------------------------------------------------------------------------

onBeforeMount(async () => {
    const layoutMenu = page.props.menuContent;

    if (layoutMenu && layoutMenu.menu) {
        menuItems.value = layoutMenu.menu;
        unprotectedMenuItems.value = layoutMenu.unprotected;
        userName.value = layoutMenu.userName;
        userId.value = layoutMenu.userId;
        multiModuleApp.value = layoutMenu.multiModuleApp;

        if (menuItems.value.length == 0) {
            console.error('Wybrany moduł (' + layoutMenu.moduleSymbol + ') nie posiada pliku json z zawartością menu. Nastąpi przekierowanie do strony wyboru modułu')
        }
    }

    clearInterval(window.displaySessionInterval);
})

//------------------------------------------------------------------------------

onMounted(async () => {
    window.endOfSession = false
    window.showDisplaySessionTime = false;
    window.lastActivityTime = null;
    isLoading.value = true;
    await initFunctions();
    isLoading.value = false;
    isCreator.value = getStoreValue('isCreator')

    const elements = document.querySelectorAll('[aria-hidden]');
    elements.forEach(element => {
        //element.setAttribute('aria-hidden', 'false');
        element.removeAttribute('aria-hidden');
    });
})

//------------------------------------------------------------------------------

const saveSession = () => {
    saveAppSession();
}

//------------------------------------------------------------------------------

const removeSession = () => {
    removeAppSession();
}

//------------------------------------------------------------------------------

const selectModule = () => {
    router.get(route('select-module', { module: getStoreValue('currentModule') }))
}

//------------------------------------------------------------------------------

</script>

<style scoped>
.v-layout {
    position: sticky;
    position: -webkit-sticky;
    z-index: 2;
    top: 0;
}

:deep(.p-menubar-start) {
    padding: 0 40px 0 20px;
    display: flex;
}

:deep(.p-menubar-root-list) {
    margin-left: auto;
}

:deep(.p-menubar-end) {
    padding: 0 20px;
}

:deep(.p-menuitem-link) {
    align-items: flex-start;
}

:deep(.p-submenu-list) {
    padding: 6px;
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 4px 6px -4px rgba(0, 0, 0, 0.08);
}

.admin-area-in-footer {
    color: var(--text-color-secondary);
    background-color: var(--surface-hover);
    border: 1px dashed var(--surface-border);
    border-radius: 4px;
    padding: 1px 6px;
    margin: 0 5px;
    font-weight: normal;
    font-size: 10px;
}
</style>
